import React, {useEffect, useState} from 'react';
import { decodeString, getQueryParameterValuesByKey } from '../_Global/searchHelpers';
import { renderHTML } from '../_Global/searchReactHelpers';
import useSearchStore from '../_Global/SearchStore';
import { FocusTrap } from 'focus-trap-react';
import useDebounce from '../_Global/useDebounce';


interface ResultJobConfiguration {
    result: SearchResult;
}

function ResultJob({result}: ResultJobConfiguration) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerIsVisible, setDrawerIsVisible] = useState(false);
    const [applyActive, setApplyActive] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [iframeHeight, setIframeHeight] = useState('100%');
    const [shareLink, setShareLink] = useState('');
    const searchTermHighlighting = useSearchStore((state) => state.searchTermHighlighting);
    const debounceCallback = useDebounce(onModalVisible, 500);

    /**
     * Operations to perform when the result first renders.
     */
    useEffect(() => {
      // Generate the share URL
      const searchPageUrlSplit = window.location.href.split('?');
      const searchAdIdParameter = getQueryParameterValuesByKey(searchPageUrlSplit[1] || '', 'meta_adId');
      const jobAdId = encodeURIComponent(result['listMetadata']['adId'][0]);
      const jobTitleEncoded = encodeURIComponent(result['listMetadata']['roleTitle'][0]);
      const subjectMessageEncoded = encodeURIComponent(' at Gilbert + Tobin');
      const bodyMessageEncoded = encodeURIComponent("If you have the ambition to work at the heart of change and take initiative, you'll thrive at G+T.\r\n\r\n");
      const jobSummaryEncoded = encodeURIComponent(result['listMetadata']['shortSummary'][0]);
      const ctaText = encodeURIComponent('\r\n\r\nView job ad:\r\n');
      const jobSearchUrlEncoded = encodeURIComponent(`${searchPageUrlSplit[0]}?meta_adId=${jobAdId}`);

      setShareLink(`mailto:?subject=${jobTitleEncoded}${subjectMessageEncoded}&body=${bodyMessageEncoded}${jobSummaryEncoded}${ctaText}${jobSearchUrlEncoded}`);

      /**
       * Automatically open the job drawer for this result
       * if there is matching Job Id in the page query string.
       * This is used for deep-linking to specific jobs.
       */
      if (
        searchAdIdParameter.length !== 0 &&
        searchAdIdParameter[0] === jobAdId
      ) {
        handleDrawerOpen();
      }
    }, []);

    /**
     * Handle the keyboard event for job modals
     * Removing on modal close or dismount to clear out duplicate events
     */
    useEffect(() => {
      const currentPageUrl = window.location.href;
      const searchAdIdParameter = getQueryParameterValuesByKey(currentPageUrl.split('?')[1] || '', 'meta_adId');
      const jobAdId = result['listMetadata']['adId'][0];

      if (drawerOpen) {
        // Listen for ESC key presses
        document.addEventListener('keydown', handleDrawerCloseKeyboardEvt);

        /**
         * Wait for the transition to complete before
         * activating the keyboard focus trap.
         */
        debounceCallback();

        /**
         * Replace the current page URL so that it includes the
         * currently open Job ID. Unless it already exists.
         *
         * This is so that page URL can be easily copied and shared.
         */
        if (searchAdIdParameter.length === 0) {
          window.history.replaceState(
            {},
            '',
            `${currentPageUrl}&meta_adId=${jobAdId}`
          );
        }

        return () => document.removeEventListener('keydown', handleDrawerCloseKeyboardEvt);
      } else {
        /**
         * Replace the current page URL to remove the Job ID.
         */
        if (searchAdIdParameter.length !== 0) {
          window.history.replaceState(
            {},
            '',
            currentPageUrl.replace(/(&meta_adId=).*?(?=&|$)/, '')
          );
        }

        // Remove ESC key listener
        document.removeEventListener('keydown', handleDrawerCloseKeyboardEvt);

        // Disable the keyboard focus trap
        setDrawerIsVisible(false);
      }

    }, [drawerOpen]);

    /**
     * Handles opening the drawer for this job.
     * Disables application in case of reopening.
     *
     * @param evt
     */
    const handleDrawerOpen = (evt?: React.MouseEvent<HTMLButtonElement>) => {
      if (evt) {
        evt.preventDefault();
      }

      setDrawerOpen(true);
      setApplyActive(false);
      setIframeLoaded(false);
    };

    /**
     * Handles closing the drawer for this job.
     * Disables application.
     *
     * @param evt
     */
    const handleDrawerClose = (evt?: React.MouseEvent<HTMLButtonElement>) => {
      if (evt) {
        evt.preventDefault();
      }

      setDrawerOpen(false);
      setApplyActive(false);
      setIframeLoaded(false);
    };

    const handleDrawerCloseKeyboardEvt = (evt: KeyboardEvent) => {
        if (evt.key === 'Escape') {
          evt.stopPropagation();
          handleDrawerClose();
        }
    };

    /**
     * Handles updating drawer content to load iframe content.
     *
     * @param evt
     */
    const handleApplyOpen = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        setApplyActive(true);
    };

    /**
     * Handles updating drawer content to load job description.
     *
     * @param evt
     */
    const handleApplyClose = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        setApplyActive(false);
        setIframeLoaded(false);
    };

    /**
     * Handles updating iframe height on load.
     * Does not work with Cross Domain access to prevent XSS vulnerabilities.
     * Defaults to 100% height.
     *
     * @param evt
     */
    const handleIframeLoaded = (evt: React.SyntheticEvent<HTMLIFrameElement>) => {
        const iframe = evt.currentTarget;

        setIframeHeight(`${iframe.contentDocument?.body.scrollHeight ?? '100%'}`);
        setIframeLoaded(true);
    };

    /**
     * Return the result summary based on the application configuration
     * and the order of preference for the content, as set here.
     */
    function getResultSummaryContent() {
      let searchSummaryContent = decodeString(result['summary']);

      if (searchTermHighlighting) {
        searchSummaryContent = result['summaryHighlighted'] || '';
      }

      if (!searchTermHighlighting && result['summaryTruncated']) {
        searchSummaryContent = result['summaryTruncated'];
      }

      return searchSummaryContent;
    }

    /**
     * Activate the focus trap
     */
    function onModalVisible() {
        /**
         * Catch the case where the drawer is closed before
         * it has time to fully transistion open;
         * thus causing a focus trap error after the
         * debounce delay.
         */
        if (drawerOpen) {
            setDrawerIsVisible(true);
        }
    }

    return (
        <div key={result['rank']} className="listing-card">
            <button
              aria-label={`View job details for ${result['listMetadata']['roleTitle']}`}
              className="listing-card__wrapper no-wysiwyg"
              id={`job_details_${result['listMetadata']['adId'][0]}`}
              onClick={handleDrawerOpen}
              role="button"
              type="button"
              >
                {result['listMetadata']['team'] ? (
                    <div className="listing-card__tag card-tag card-tag--alt body-size-lg">
                        <svg className="card-tag__svg">
                            <use xlinkHref="#square"></use>
                        </svg>
                        <span className="card-tag__text">{result['listMetadata']['team']}</span>
                    </div>
                ) : (
                    ``
                )}

                <div className="listing-card__grid grid sm:grid-cols-8 sm:gap-8 md:gap-16 lg:gap-0">
                    <div className="sm:col-span-3 lg:col-span-3">
                        <h3 className="listing-card__heading heading-size-md">{result['listMetadata']['roleTitle']}</h3>

                        <div className="listing-card__metadata metadata-wrapper metadata-wrapper--inline">
                            <div className="metadata-block metadata-block--inline">
                                <svg className="metadata-block__svg body-size-lg">
                                    <use xlinkHref="#map-pin"></use>
                                </svg>
                                <span className="metadata-block__value body-size-lg">{result['listMetadata']['location']}</span>
                            </div>
                            <span className="vertical-divider" role="presentation"></span>
                            <div className="metadata-block metadata-block--inline">
                                <span className="metadata-block__value body-size-lg">{result['listMetadata']['roleType']}</span>
                            </div>
                        </div>
                    </div>

                    <div className="sm:col-span-4 lg:col-span-3 lg:col-end-8">
                      { renderHTML('p', 'listing-card__text body-size-lg', getResultSummaryContent()) }
                    </div>

                    <div className="listing-card__arrow-link button">
                        <svg role="graphics-symbol" className="icon__arrow icon__arrow--diagonal-animation">
                            <use xlinkHref="#arrow-diagonal"></use>
                        </svg>
                    </div>
                </div>
            </button>

            <FocusTrap active={drawerIsVisible && drawerOpen}>
              <div
                aria-hidden={!drawerOpen}
                aria-labelledby={`job_details_${result['listMetadata']['adId'][0]}`}
                aria-modal="true"
                className={`job-drawer-modal__wrapper ${drawerOpen ? 'job-drawer-modal--active' : ''}`}
                data-component="job-drawer"
                role="dialog"
                >
                <button
                  aria-hidden="true"
                  className="job-drawer-modal__hidden-button-close no-wysiwyg"
                  data-role="dialog-close-button"
                  onClick={handleDrawerClose}
                  role="button"
                  tabIndex={-1}
                  type="button"
                  >
                  <span className="sr-only">Close job details</span>
                </button>

                <aside className="job-drawer-modal__content">
                    <div className="job-drawer-modal__header no-wysiwyg">
                        <button
                          className="job-drawer-modal__button-close"
                          data-role="dialog-close-button"
                          onClick={handleDrawerClose}
                          role="button"
                          type="button"
                          >
                            <svg>
                                <use xlinkHref="#cross"></use>
                            </svg>
                            <span className="sr-only">Close job details</span>
                        </button>
                    </div>
                    <div className="job-drawer__content-scroller" tabIndex={1}>
                        <div className={applyActive ? 'hidden' : ''}>
                            <h2 className="heading-size-lg mb-6 no-wysiwyg">{result['listMetadata']['roleTitle']}</h2>

                            {renderHTML('div', '', result['listMetadata']['fullDescription'])}
                        </div>
                        {applyActive && (
                            <>
                                <div className={`loading-indicator__container no-wysiwyg ${iframeLoaded ? 'hidden' : ''}`}>
                                    <div className="loading-indicator__spinner"></div>
                                    <p className="heading-size-md">Loading application</p>
                                </div>
                                <iframe
                                    src={result['listMetadata']['applyUrl']}
                                    title={result['listMetadata']['roleTitle']}
                                    height={iframeHeight}
                                    width={'100%'}
                                    className={iframeLoaded ? 'no-wysiwyg' : 'hidden'}
                                    onLoad={handleIframeLoaded}
                                />
                            </>
                        )}
                    </div>
                    <div className="job-drawer-modal__footer no-wysiwyg">
                        {!applyActive && (
                            <button className="button button--primary" type="button" role="button" onClick={handleApplyOpen}>
                                <span className="button__animate-element"></span>
                                <span className="button__label button-default">Apply now</span>
                                <svg className="button__icon">
                                    <use xlinkHref="#arrow-side"></use>
                                </svg>
                            </button>
                        )}
                        {applyActive && (
                            <button className="button button--secondary button--cancel" type="button" role="button" onClick={handleApplyClose}>
                                <span className="button__label button-default">Cancel</span>
                                <svg className="button__icon">
                                    <use xlinkHref="#cross"></use>
                                </svg>
                            </button>
                        )}
                        <a href={shareLink} className="button button--secondary button--share">
                          <span className="button__label button-default">Share this job</span>
                          <svg className="button__icon"><use xlinkHref="#email"></use></svg>
                        </a>
                    </div>
                </aside>
              </div>
            </FocusTrap>
        </div>
    );
}

/**
 * Create the markup for this job result.
 */
export default React.memo(ResultJob);
